import { useState, useEffect, useRef } from "react";
import { GrFormClose } from "react-icons/gr";
import { AiFillStar } from "react-icons/ai";
import axios from "axios";

const FeedbackPortal = ({
  isOpen,
  onClose,
  onFeedbackSubmit,
  userId,
  emailId,
  productId = "999",
}) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [hoveredText, setHoveredText] = useState("");
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!isOpen) return;
      try {
        const res = await axios.get(
          "https://auth.ssccglpinnacle.com/api/v1/ebook/get-feedback",
          { params: { userId, productId } }
        );
        if (res.data) {
          const { rating, feedback } = res.data;
          setRating(rating);
          setFeedback(feedback);
          setHoveredText(getStarLabel(rating));
        }
      } catch (err) {
        console.error("Error loading feedback:", err);
      }
    };
    fetchFeedback();
  }, [isOpen, userId, productId]);

  const handleSubmit = async () => {
    try {
      const payload = { userId, productId, rating, feedback, emailId };
      await axios.post(
        "https://auth.ssccglpinnacle.com/api/v1/typing/submit-feedback",
        payload
      );
      onFeedbackSubmit(rating, feedback);
      onClose();
    } catch (err) {
      console.error("Submission error:", err);
    }
  };

  const getStarLabel = (val) => {
    return ["Awful", "Poor", "Average", "Good", "Amazing"][val - 1] || "";
  };

  const handleStarClick = (val) => {
    setRating(val);
    setHoveredText(getStarLabel(val));
  };

  const handleStarHover = (val) => {
    setHoverRating(val);
    setHoveredText(getStarLabel(val));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div
        className="relative bg-white p-6 sm:p-8 rounded-lg shadow-xl w-[90%] max-w-lg text-center"
        ref={modalRef}
      >
        <button
          className="absolute top-2 right-2 text-gray-600 hover:text-black"
          onClick={onClose}
        >
          <GrFormClose size={22} />
        </button>

        <h2 className="text-lg font-bold mb-1">
          How would you rate this software?
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {hoveredText || "Select Rating"}
        </p>

        {/* Star Rating */}
        <div className="flex justify-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((val) => (
            <button
              key={val}
              onClick={() => handleStarClick(val)}
              onMouseEnter={() => handleStarHover(val)}
              onMouseLeave={() => handleStarHover(rating)}
              className="text-3xl"
              title={getStarLabel(val)}
            >
              <AiFillStar
                color={val <= (hoverRating || rating) ? "#f1c40f" : "#d1d7dc"}
              />
            </button>
          ))}
        </div>

        {/* Feedback TextArea */}
        <textarea
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm h-28 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          placeholder="Write your feedback here (optional)..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition text-sm font-semibold"
        >
          Submit Feedback
        </button>
      </div>
    </div>
  );
};

export default FeedbackPortal;
